interface AmaRedirectData {
	baseUrl: string;
	redirectUri?: string;
	clientId?: string;
	scope?: string | string[];
	responseType?: string;
	state?: string;
}

interface AmaRedirectOptions {
	storage?: 'session';
}

interface AmaRedirectInput {
	data: AmaRedirectData;
	options?: AmaRedirectOptions;
}

export enum AmaError {
	cancelled = 'cancelled',
	invalidRequest = 'invalid_request',
}

export interface AmaHashParams {
	accessToken?: string;
	tokenType?: string;
	expiresIn?: string;
	refreshToken?: string;
	error?: AmaError | string;
	state?: string;
}

const SESSION_STORAGE_KEY = 'ama-helper-storage';

export class AmaHelper {
	/**
	 * Redirects to Ama url within same tab.
	 * @param AmaRedirectInput => { data: AmaRedirectData; options?: AmaRedirectOptions }
	 * @returns void
	 */
	public static redirect = ({ data, options }: AmaRedirectInput): void => {
		const { baseUrl, redirectUri, clientId, scope, responseType = 'token', state } = data;

		const amaUrl = buildUrl(baseUrl, [
			{ key: 'redirect_uri', value: redirectUri },
			{ key: 'client_id', value: clientId },
			{ key: 'scope', value: typeof scope === 'string' ? scope : encodeArrayQueryParams(scope) },
			{ key: 'response_type', value: responseType },
			{ key: 'state', value: state },
		]);

		if (options?.storage === 'session') {
			this.setSessionStorage(data);
		}

		window.open(amaUrl, '_self', 'noopener noreferrer');
	};

	/**
	 * Gets the hash params returned from Ama redirect.
	 * @returns AmaHashParams
	 */
	public static getHashParams = (): AmaHashParams => {
		const hash = window.location.hash; // Get the part after #
		const params: { [key: string]: string | undefined } = {};

		if (hash.startsWith('#')) {
			const queryString = hash.substring(1); // Remove the #
			const pairs = queryString.split('&');
			pairs.forEach((pair) => {
				const [key, value] = pair.split('=');
				if (key) {
					params[decodeURIComponent(key)] = value ? decodeURIComponent(value) : undefined;
				}
			});
		}

		return {
			accessToken: params.access_token || '',
			tokenType: params.token_type || '',
			expiresIn: params.expires_in || '',
			refreshToken: params.refresh_token || '',
			error: params.error || '',
			state: params.state || '',
		};
	};

	/**
	 * Verifies if is Ama redirect depending on the state hash param.
	 * @returns True when hashParams.state equals sessionStorage.state. Undefined when no state provided in "redirect" function.
	 */
	public static isAmaRedirect = (): boolean | undefined => {
		const sessionStorage = this.getSessionStorage();
		const sessionStorageState = sessionStorage?.state;
		const hashParams = this.getHashParams();
		const hashParamsState = hashParams.state;

		if (!sessionStorageState) {
			return;
		} else if (sessionStorageState === hashParamsState) {
			return true;
		} else {
			return false;
		}
	};

	public static setSessionStorage = (data: AmaRedirectData) => {
		const jsonString = JSON.stringify(data);
		sessionStorage.setItem(SESSION_STORAGE_KEY, jsonString);
	};

	public static getSessionStorage = (): AmaRedirectData | undefined => {
		try {
			const jsonString = sessionStorage.getItem(SESSION_STORAGE_KEY);
			if (jsonString) {
				return JSON.parse(jsonString) as AmaRedirectData;
			}
		} catch {
			return;
		}
	};

	public static clearSessionStorage = (): void => {
		sessionStorage.removeItem(SESSION_STORAGE_KEY);
	};

	public static clearHashParams = () => {
		window.history.replaceState(null, '', window.location.pathname + window.location.search);
	};
}

export const buildUrl = (
	baseUrl: string,
	queryParams: Array<{ key: string; value?: string | string[] | null; encode?: boolean }>
) => {
	return `${baseUrl}${createQueryParams(queryParams)}`;
};

export const createQueryParams = (
	values?: Array<{ key: string; value?: string | string[] | null; encode?: boolean }>
) => {
	const query = values?.map(({ value, key, encode }) => {
		const nonNullValue = value?.toString() || '';
		return `${key}=${encode ? encodeURIComponent(nonNullValue) : nonNullValue}`;
	});

	return query?.length ? '?' + query.join('&') : '';
};

export const encodeArrayQueryParams = (values?: string[]) => {
	if (!values?.length) {
		return;
	}

	let encodedValue = '';
	values.forEach((value) => {
		encodedValue = `${encodedValue}${value}${encodeURIComponent(' ')}`;
	});
	return encodedValue;
};
